import { BaseQueryFn } from "@reduxjs/toolkit/query";
import { message } from "antd";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { serialize } from "object-to-formdata";
import { buildPath } from "../utils/helpers";

import {
  getLocalStorageAccessToken,
  getLocalStorageRefreshToken,
  // setLocalStorageAccessToken,
} from "../utils/helpers/localStorage";
import { env } from "../env";
import { APP_URL /*, DEFAULT_LANGUAGE*/, getAppUrl } from "../utils/defines";
import { isTokenValid, removeAuthTokens } from "../utils/helpers/auth";
import { getMemAppToken, getUserTokenAsync } from "utils/helpers/project";
import { jwtDecode } from "jwt-decode";

let promisUserToken: Promise<any> | null = null;
let promisRefreshToken: Promise<any> | null = null;
export const api = axios.create({
  baseURL: `${env.api_url}/api`,
  //headers: { LanguageCode: DEFAULT_LANGUAGE },
  //withCredentials: true
});

api.interceptors.response.use(
  async (config) => {
    if (config.data.hasError) {
      message.error(config.data.message || "Something went wrong");
    }
    return config;
  },
  async (err) => {
    let redirectUrl;
    if (err.response?.status === 401 || !err.response) {
      const refreshToken = getLocalStorageRefreshToken();
      if (refreshToken && refreshToken !== "undefined") {
        //
        if (!promisRefreshToken) {
          promisRefreshToken = new Promise(
            async (resolve: any, reject: any) => {
              const new_token = await getUserTokenAsync();
              if (new_token) {
                resolve(new_token);
                promisRefreshToken = null;
              } else {
                reject();
              }
            }
          );
        }

        let userToken = null;
        if (promisRefreshToken != null) {
          userToken = await promisRefreshToken
            .then(async (resolve: any) => {
              return resolve;
            })
            .catch(async () => {
              return null;
            });
        }
        //const userToken = await getUserTokenAsync();
        if (!userToken) {
          removeAuthTokens();
          redirectUrl = getAppUrl(APP_URL.Login);
        }
      } else {
        removeAuthTokens();
        redirectUrl = getAppUrl(APP_URL.Login);
      }
    } else if (err.response.status === 403) {
      redirectUrl = getAppUrl("/");
    }

    if (redirectUrl) {
      window.location.href = redirectUrl
    }

    throw err;
  }
);

api.interceptors.request.use(async (config) => {
  let user_token = getLocalStorageAccessToken() ?? getMemAppToken();
  if (user_token) {
    const decoded = jwtDecode(user_token);
    const expirationDate = new Date(0);
    expirationDate.setUTCSeconds(decoded.exp!);
    const currentDate = new Date();
    if (expirationDate.getTime() - currentDate.getTime() < 5 * 1000 * 60) {
      if (!promisUserToken) {
        promisUserToken = new Promise(async (resolve: any, reject: any) => {
          const new_token = await getUserTokenAsync();
          if (new_token) {
            resolve(new_token);
            promisUserToken = null;
          } else {
            reject();
          }
        });
      }

      if (promisUserToken != null) {
        user_token = await promisUserToken
          .then(async (resolve: any) => {
            return resolve;
          })
          .catch(async () => {
            return null;
          });
      }
      //user_token = await getUserTokenAsync();
    }

    if (user_token) {
      const { aut } = jwtDecode<{ aut: string }>(user_token);
      if (aut === "APPLICATION" || isTokenValid(user_token)) {
        config.headers.Authorization = "Bearer " + user_token;
      } else {
        removeAuthTokens();
        window.location.href = getAppUrl(APP_URL.Login);
        return Promise.reject("Unauthorized");
      }
    } else {
      window.location.href = getAppUrl("/");
    }
  } else {
    const app_token = getMemAppToken();
    if (app_token) {
      config.headers.Authorization = "Bearer " + app_token;
    }
  }
  return config;
});

export const query =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: "" }
  ): BaseQueryFn<
    {
      url: string;
      baseUrl?: string;
      method: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
      isJson?: boolean;
    },
    unknown,
    unknown
  > =>
  async ({
    url,
    method,
    data,
    params,
    headers,
    baseUrl: overriddenBaseUrl,
    isJson,
  }) => {
    if (data && !isJson) {
      data = formDataFromObject(data);
    }
    try {
      const result = await api({
        url: buildPath(
          typeof overriddenBaseUrl === "string" ? overriddenBaseUrl : baseUrl,
          url
        ),
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export function formDataFromObject(obj: any) {
  return serialize(obj, { indices: true });
}

export const setAuthHeader = (token: string) => {
  api.defaults.headers.Authorization = "Bearer " + token;
};

export const setLanguageHeader = (code: string) => {
  api.defaults.headers.LanguageCode = code;
};

export const removeAuthHeader = () => {
  api.defaults.headers.Authorization = null;
};
